import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import qs from 'qs';
import FilterList from './FilterList';
import { __t } from './i18n';
import { Badge, Box, Button, Flex, Icon, Link, Paper, Row, Spinner, Tag } from './styles';
import { IconDeleteCircle, IconFilter } from './icons';
import { ParsedUrlQuery } from 'node:querystring';
import SearchInput from './SearchInput';
import Dialog from './Dialog';
import styled from '@emotion/styled';
import { CategoryList, ContentFlag, ContentSortOrder, ContentType } from './apiService';

const options: {
  order: ContentSortOrder[];
  types: ContentType[];
  flags: ContentFlag[];
} = {
  order: ['default', 'latest', 'most_visited', 'rate'], // 'best_selling',
  types: ['single_video', 'series', 'video_content_list'],
  flags: ['coming_soon', 'has_subtitle', 'persian_dub', 'exclusive_dub'], // 'hd', '_4_k', 'multi_audio', 'free_content', 'traffic_free',
};

const staticFilters: FilterInputType[] = [
  {
    label: 'type',
    field: 'types',
    type: 'multi',
    options: options.types.map((value) => ({ label: __t('filter.type.' + value), value })),
  },
  {
    label: 'order',
    field: 'order',
    type: 'single',
    options: options.order.map((value) => ({ label: __t('filter.order.' + value), value })),
  },
  {
    label: 'flags',
    field: 'flags',
    type: 'multi',
    options: options.flags.map((value) => ({ label: __t('flag.' + value), value })),
  },
];

const LoadingWrapper = styled(Flex)(({ theme }) => ({
  ...theme.font.responsiveSize(5),
  position: 'absolute',
  inset: 0,
  zIndex: theme.zIndex.modal,
  pointerEvents: 'none',
  opacity: .5,
}));

const Loading = () => <LoadingWrapper align="center" justify="center"><Spinner block /></LoadingWrapper>;

const initState: StateType = {
  order: '', // select
  types: [], // multi-select
  flags: [], // multi-select
  categories: [], // multi-select
  year: '',
  query: '',
};

type ContentFilterProps = {
  category_filters: CategoryList[];
  loading?: boolean;
}

const BadgeWrapper = styled.span(() => ({
  display: 'inline-block',
  marginRight: '.5em',
  marginTop: '-.5em',
  verticalAlign: 'middle',
  [`${Badge}`]: {
    minWidth: '1.25em',
    padding: '.25em .25em 0 .25em',
  },
}));

export default function ContentFilter({ category_filters, loading }: ContentFilterProps) {
  const router = useRouter();
  const { pathname, query } = router;
  const [state, setState] = useState<StateType>({ ...initState, ...queryToState(query) });
  const [open, setOpen] = useState<boolean>(false);
  const queryString = stateToQueryString(state);
  const redirect = useRef(true);

  const filters: FilterInputType[] = [
    ...staticFilters,
    ...category_filters.map(
      ({ type, items }: CategoryList): FilterInputType => ({
        label: type,
        field: 'categories',
        type: 'multi',
        options: items.map(({ title: label, id: value }) => ({ label, value })),
      }),
    ),
  ];
  const selectedFilters: SelectedFilterType[] = getSelectedFilters({ ...state }, [...filters]);

  const handleChange = (key: string, value: string, type: string) => {
    redirect.current = true;
    if (type === 'multi') {
      let currentValue = state[key];
      if (!value) {
        currentValue = [];
      } else {
        const index = currentValue.indexOf(value);
        if (index === -1) {
          currentValue = [...currentValue, value];
        } else {
          currentValue = currentValue.filter((_: any, i: number) => i !== index);
        }
      }
      setState({ ...state, [key]: currentValue });
    } else if (state[key] !== value) {
      setState({ ...state, [key]: value });
    }
  };

  const handleClearAll = () => {
    redirect.current = true;
    setState({ ...initState });
    router.push(pathname, undefined, { shallow: true });
  };

  useEffect(() => {
    redirect.current = false;
    setState({ ...initState, ...queryToState(query) });
  }, [query]);

  useEffect(() => {
    if (redirect.current) {
      const timeoutId = setTimeout(() => {
        router.push({ pathname, query: queryString }, undefined, { shallow: true });
      }, 500);
      return () => { clearTimeout(timeoutId); };
    }
  }, [queryString, pathname, router]);

  return (
    <Paper mb={4} style={{ position: 'relative' }}>
      {loading && <Loading />}

      {/* FILTERS: DESKTOP */}
      <Box className="desktop-only">
        <FilterList
          filters={filters}
          value={state}
          onChange={handleChange}
        />
      </Box>

      {/* FILTERS: MOBILE */}
      <Box className="mobile-only">
        <Button
          onClick={() => setOpen(true)}
          fullWidth
          iconStart
          variant="primaryA"
        >
          <Icon cursor="pointer"><IconFilter /></Icon>
          {__t('filter.title')}
          {!!selectedFilters?.length && <BadgeWrapper><Badge color="foreground">{selectedFilters.length}</Badge></BadgeWrapper>}
        </Button>
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
        >
          <FilterList
            filters={filters}
            value={state}
            onChange={handleChange}
          />
          <Button
            variant="primaryA"
            onClick={() => setOpen(false)}
            mt={3}
            fullWidth
          >
            {__t('common.submit')}
          </Button>
        </Dialog>
      </Box>

      {/* SEARCH BOX + CLEAR FILTERS */}
      <Flex
        direction={{ xs: 'column-reverse', md: 'row' }}
        justify={{ xs: 'flex-start', md: 'space-between' }}
        mt={{ md: 5 }}
      >
        <SearchInput
          autoFocus={false}
          color="dark"
          value={state.query}
          onChange={(value) => { handleChange('query', value, 'single'); }}
        />
        <Button
          variant="secondary"
          mr={{ xs: 0, md: 4 }}
          my={{ xs: 3, md: 0 }}
          onClick={handleClearAll}
          disabled={queryString === ''}
        >
          {__t('filter.clear_all')}
        </Button>
      </Flex>

      {/* SELECTED FILTERS */}
      <Row mt={2}>
        {selectedFilters?.map(({ field, type, label, value }) => (
          <Tag
            as={Link}
            href="#"
            key={field + value}
            onClick={(e) => { e.preventDefault(); handleChange(field, value, type); }}
          >
            {label} <Icon ml="-.5em" mr=".25em" cursor="pointer"><IconDeleteCircle /></Icon>
          </Tag>
        ))}
      </Row>
    </Paper>
  );
}

function queryToState(query: ParsedUrlQuery): StateType {
  const result = {};
  Object.keys(query).forEach((field) => {
    if (Array.isArray(query[field])) {
      result[field] = query[field];
    } else {
      result[field] = (Array.isArray(initState[field])) ? [query[field]] : query[field];
    }
  });

  return result;
}

export function stateToQueryString(object: StateType): string {
  const result = { ...object };
  Object.keys(result).forEach((key) => {
    if (result[key] === '') delete result[key];
  });
  return qs.stringify(result, { indices: false });
}

function getSelectedFilters(state: StateType, filters: FilterInputType[]): SelectedFilterType[] {
  let result: SelectedFilterType[] = [];
  filters.forEach(({ field, type, options }) => {
    const selected = options
      .filter(() => type === 'multi')
      .filter(({ value }) => state[field].includes(value))
      .map(({ label, value }) => ({ label, value, field, type }));
    result = [...result, ...selected];
  });
  return result;
}

type SelectedFilterType = FilterOptionType & Omit<FilterInputType, 'label' | 'options'>;

export interface FilterOptionType {
  label: string;
  value: string;
}

export interface FilterInputType {
  label: string;
  field: string;
  type: 'single' | 'multi';
  options: FilterOptionType[];
}

export interface StateType {
  order?: string;
  types?: string[];
  flags?: string[];
  categories?: string[];
  year?: string;
  query?: string;
}
